import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  input,
  linkedSignal,
  output,
  signal,
  viewChild,
} from '@angular/core';

import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { translate } from '@jsverse/transloco';
import Highcharts, { Chart, SeriesLineOptions } from 'highcharts';

import { combineLatest, debounceTime, filter } from 'rxjs';
import { DropdownObject, DropdownGroup } from '@inmoment/the-kitchen';

import { AppSharedModule } from '../../../../../../app-shared.module';

import { DocumentViewSection } from '../../../../../../providers/nlp-output.service';
import { VectorMetric } from '../../../../../../providers/storage-api/document.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from '../../../../../../services/otel.service';
import { SENTIMENT_HEX_COLOR_3_SCALE } from '../../../sentiment-display/sentiment-display.component';
import { DocumentScoringMetricsSectionComponent } from '../document-scoring-metrics-section/document-scoring-metrics-section.component';
import {
  DocumentScoringPanel,
  DocumentScoringPanelType,
} from '../document-scoring-metrics.component';

export interface DocumentScoringMetricsOvertimeSettings {
  metric: string;
  groupBy: string;
  speaker?: string;
  sectionIds?: number[];
  panels?: { [key in DocumentScoringPanelType]?: DocumentScoringPanel };
}

const SPEAKER_UNSET = '__SPEAKER_UNSET';

@EnableClassTelemetry()
@Component({
  selector: 'app-document-scoring-metrics-overtime-section',
  imports: [AppSharedModule, DocumentScoringMetricsSectionComponent],
  templateUrl: './document-scoring-metrics-overtime-section.component.html',
  styleUrl: './document-scoring-metrics-overtime-section.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class DocumentScoringMetricsOvertimeSectionComponent {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  sections = input<DocumentViewSection[]>();
  vectorMetrics = input<VectorMetric[]>([]);
  settings = input<DocumentScoringMetricsOvertimeSettings>();
  panel = input<DocumentScoringPanel>();

  selectSection = output<DocumentViewSection>();
  settingsChanged = output<DocumentScoringMetricsOvertimeSettings>();
  expandedChanged = output<DocumentScoringPanel>();

  chartContainer = viewChild<ElementRef>('metricChart');

  metricOptions = computed<DropdownObject[]>(() => {
    const options = this.vectorMetrics().map((m) => ({ id: m.scoreName, title: m.scoreName }));
    if (this.hasSectionSentiment) {
      options.push({
        id: 'nlp-sentiment',
        title: translate('documents.scoring-metrics.overtime-section.sentiment'),
      });
    }
    return options;
  });
  metricOptionGroups = computed<DropdownGroup[]>(() => {
    const groups = [];
    if (this.hasSectionSentiment) {
      groups.push({
        title: translate('documents.scoring-metrics.overtime-section.nlp-scores'),
        ids: ['nlp-sentiment'],
      });
    }

    const smartScoreIds = this.metricOptions()
      .filter((o) => o.id !== 'nlp-sentiment')
      .map((o) => o.id);

    if (smartScoreIds.length) {
      groups.unshift({
        title: translate('documents.scoring-metrics.overtime-section.smart-scores'),
        ids: smartScoreIds,
      });
    }

    return groups;
  });

  metricGroupByOptions = computed<DropdownObject[]>(() => {
    const addSegmentsIfPresent = (options) => {
      if (this.hasSectionSegment) {
        options.push({
          id: 'segments',
          title: translate('documents.scoring-metrics.overtime-section.segments'),
        });
      }
    };

    const addTimestampsIfPresent = (options) => {
      if (this.hasSectionTimestamp) {
        options.push({
          id: 'timestamps',
          title: translate('documents.scoring-metrics.overtime-section.timestamps'),
        });
      }
    };

    const groupByOptions = [];

    switch (this.selectedMetric()) {
      case 'nlp-sentiment':
        addSegmentsIfPresent(groupByOptions);
        addTimestampsIfPresent(groupByOptions);

        if (!groupByOptions.length) {
          groupByOptions.push({
            id: 'sections',
            title: translate('documents.scoring-metrics.overtime-section.sections'),
          });
        }

        return groupByOptions;
      default:
        groupByOptions.push({
          id: 'keymoments',
          title: translate('documents.scoring-metrics.overtime-section.key-moments'),
        });

        addSegmentsIfPresent(groupByOptions);
        addTimestampsIfPresent(groupByOptions);

        return groupByOptions;
    }
  });

  metricSpeakerOptions = computed<DropdownObject[]>(() => {
    const options = [...new Set(this.sections().map((s) => s.label))].map((s) => ({
      id: s ?? SPEAKER_UNSET,
      title: s ?? translate('documents.scoring-metrics.overtime-section.speaker-empty-parenthesis'),
    }));
    options.unshift({
      id: 'all',
      title: translate('documents.scoring-metrics.overtime-section.all'),
    });
    return options;
  });

  selectedMetric = linkedSignal<string>(() => {
    // Load initial value if it's available
    const selectedMetric = this.settings()?.metric;

    return selectedMetric && this.metricOptions().some((o) => o.id === selectedMetric)
      ? selectedMetric
      : this.metricOptions()[0].id;
  });
  selectedMetricGroupBy = linkedSignal<DropdownObject[], string>({
    source: this.metricGroupByOptions,
    computation: (groupByOptions, previous) => {
      // Load initial value if it's available
      const selectedGroupBy = this.settings()?.groupBy;
      if (groupByOptions.some((o) => o.id === selectedGroupBy)) {
        return selectedGroupBy;
      }

      if (groupByOptions.some((o) => o.id === previous?.value)) {
        return previous.value;
      }

      switch (this.selectedMetric()) {
        case 'nlp-sentiment':
          if (!this.hasSectionTimestamp) {
            if (!this.hasSectionSegment) {
              return 'sections';
            }
            return 'segments';
          }
          return 'timestamps';
        default:
          return 'keymoments';
      }
    },
  });
  selectedMetricSpeaker = linkedSignal<DropdownObject[], string>({
    source: this.metricSpeakerOptions,
    computation: (speakerOptions) => {
      // Load initial value if it's available
      const selectedSpeaker = this.settings()?.speaker;
      return selectedSpeaker &&
        speakerOptions.some(
          (o) => o.id === selectedSpeaker || (selectedSpeaker === SPEAKER_UNSET && !o.id)
        )
        ? selectedSpeaker
        : 'all';
    },
  });

  selectedSectionIds = computed<number[]>(() => this.settings()?.sectionIds);

  chartTitle = computed(() => {
    if (this.selectedMetric() === 'nlp-sentiment') {
      let speaker = this.selectedMetricSpeaker();
      if (speaker === SPEAKER_UNSET) {
        speaker = translate('documents.scoring-metrics.overtime-section.speaker-empty');
      }

      return translate(
        `documents.scoring-metrics.overtime-section.chart-sentiment-by-${this.selectedMetricGroupBy()}`,
        {
          speaker,
        }
      );
    }

    return translate(
      `documents.scoring-metrics.overtime-section.chart-score-by-${this.selectedMetricGroupBy()}`,
      {
        smartScore: this.selectedMetric(),
      }
    );
  });

  totalChangeLabel = computed(() => {
    if (this.selectedMetric() === 'nlp-sentiment') {
      return translate('documents.scoring-metrics.overtime-section.sentiment-trend');
    }
    return translate('documents.scoring-metrics.overtime-section.metric-change', {
      metric: this.selectedMetric(),
    });
  });
  totalChangeValue = signal(0);
  totalChangeValueSign = computed(() => {
    const sgn = Math.sign(this.totalChangeValue());
    if (sgn > 0) {
      return '+';
    }
    return '';
  });

  private _chart: Chart;
  isChartEmpty = signal(true);

  constructor() {
    combineLatest([
      toObservable(this.selectedMetric),
      toObservable(this.selectedMetricGroupBy),
      toObservable(this.selectedMetricSpeaker),
      toObservable(this.selectedSectionIds),
      toObservable(this.chartContainer),
    ])
      .pipe(
        takeUntilDestroyed(),
        filter(([_1, _2, _3, _4, container]) => !!container),
        debounceTime(10)
      )
      .subscribe(([metric, groupBy, speaker, sectionIds, container]) => {
        // If it's not necessary to create/recreate chart, then just simply refresh the selection
        if (!this.shouldCreateChart(metric, groupBy, speaker)) {
          this.refreshChartPointSelection(sectionIds);
          return;
        }

        const self = this;
        const chartOptions = {
          title: null,
          credits: { enabled: false },
          legend: {
            enabled: false,
          },
          exporting: {
            buttons: { contextButton: { enabled: false } },
            fallbackToExportServer: false,
          },
          chart: {
            type: 'line',
          },
          plotOptions: {
            series: {
              allowPointSelect: true,
              point: {
                events: {
                  click() {
                    self.onPointClicked(this);
                  },
                },
              },
            },
          },
          xAxis: {
            type: 'category',
            tickInterval: 1,
          },
          yAxis: {
            title: null,
          },
          tooltip: {
            pointFormat: '<b>{point.y:.2f}</b>',
          },
        } as Highcharts.Options;

        if (groupBy === 'timestamps') {
          const xAxis = chartOptions.xAxis as Highcharts.XAxisOptions;
          xAxis.type = 'datetime';
          delete xAxis.tickInterval;
          // DASH-9971: On a datetime axis, Highcharts aggressively hides markers outside the hover tolerance / crop range,
          // and the hover state temporarily replaces the select state, causing the marker to disappear.
          // Therefore, we force markers to appear for all points so that the selection remains visible.
          chartOptions.plotOptions.series.marker = {
            enabled: true,
          };
        }

        if (groupBy === 'keymoments') {
          chartOptions.tooltip.pointFormat += ' <em>{point.options.custom.justification}</em>';
        }

        // Emit settingsChanged event to store actual state at the parent components.
        this.onSettingsChanged({
          ...this.settings(),
          metric: metric,
          groupBy: groupBy,
          speaker: speaker,
        });

        chartOptions.series = this.createChartSeries(metric, groupBy, speaker);

        if (!(chartOptions.series[0] as any).data.length) {
          this.isChartEmpty.set(true);
          return;
        }

        this.isChartEmpty.set(false);
        this._chart = Highcharts.chart(container.nativeElement, chartOptions);

        // Refresh the point selection to display the previously selected or most relevant point to the user.
        this.refreshChartPointSelection(sectionIds);
      });
  }

  private shouldCreateChart(metric, groupBy, speaker) {
    // Create the chart if it hasn’t been created yet or if the main settings have changed.
    // Note: settings() signal contains the initial values before any changes.
    return (
      !this._chart ||
      this.settings()?.metric !== metric ||
      this.settings()?.groupBy !== groupBy ||
      this.settings()?.speaker !== speaker
    );
  }

  private shouldEmitSelectSection(metric, groupBy, speaker) {
    // Emit only if the main settings have changed.
    return (
      this.selectedMetric() !== metric ||
      this.selectedMetricGroupBy() !== groupBy ||
      this.selectedMetricSpeaker() !== speaker
    );
  }

  private createChartSeries(metric, groupBy, speaker): SeriesLineOptions[] {
    if (metric === 'nlp-sentiment') {
      return this.createChartSeriesForSentiment(groupBy, speaker);
    }
    return this.createChartSeriesForSmartScore(metric, groupBy);
  }

  private refreshChartPointSelection(sectionIds: number[]) {
    if (!this._chart) {
      return;
    }
    this._chart.series.forEach((series) => {
      let selectedPoint: Highcharts.PointOptionsObject | undefined;

      series.data.forEach((point) => {
        let selected = false;
        const isAlreadySelected = !!point.selected;

        if (!selectedPoint && sectionIds) {
          selected = sectionIds
            .sort((a, b) => a - b)
            .some((id) => (point.options?.custom?.sectionIds ?? []).includes(id));
          if (selected && !isAlreadySelected) {
            selectedPoint = point;
          }
        }

        if (selected !== isAlreadySelected) {
          point.select(selected);
        }
      });
    });

    this._chart.redraw();
  }

  onExpandedChanged(panel: DocumentScoringPanel) {
    this.expandedChanged.emit(panel);
  }

  private onSettingsChanged(settings: DocumentScoringMetricsOvertimeSettings) {
    // Emit selectSection on settingsChanged only if it's necessary
    const sectionToEmit = this.sections().find((s) =>
      settings.sectionIds?.some((sId) => sId === s.sectionId)
    );
    if (
      this.shouldEmitSelectSection(settings.metric, settings.groupBy, settings.speaker) &&
      sectionToEmit
    ) {
      this.selectSection.emit(sectionToEmit);
    }

    // Emit settings changed event
    this.settingsChanged.emit(settings);
  }

  private createChartSeriesForSentiment(groupBy, speaker) {
    const data = [];
    const sectionFilterFn = (s) => {
      if (s.sentimentScore == null) {
        return false;
      }

      if (speaker === SPEAKER_UNSET) {
        return !s.label;
      }

      return speaker === 'all' || s.label === speaker;
    };

    switch (groupBy) {
      case 'sections':
        data.push(
          ...this.sections()
            .filter(sectionFilterFn)
            .map((s) => ({
              x: s.sectionId,
              y: s.sentimentScore,
              color: this.colorForValue(s.sentimentScore),
              custom: {
                sectionIds: [s.sectionId],
              },
            }))
        );
        break;
      case 'segments':
        data.push(...this.extractDataForSegment(sectionFilterFn, (s) => s.sentimentScore));
        break;
      case 'timestamps':
        this.sections()
          .filter(sectionFilterFn)
          .sort((s1, s2) => s1.date.getTime() - s2.date.getTime())
          .forEach((s) => {
            data.push({
              x: s.date.getTime(),
              y: s.sentimentScore,
              color: this.colorForValue(s.sentimentScore),
              custom: {
                sectionIds: [s.sectionId],
              },
            });
          });
        break;
    }

    const series: any[] = [
      {
        type: 'line' as const,
        data,
      },
    ];

    const trendSeries = this.createTrendlineSeries(data);
    if (trendSeries) {
      series.push(trendSeries);
      this.totalChangeValue.set(trendSeries?.custom.slope ?? 0);
    }

    return series;
  }

  private createTrendlineSeries(data): SeriesLineOptions {
    const xData = data.map((_, i) => i);
    const yData = data.map((p) => p.y);

    if (xData.length < 2) {
      return null;
    }

    // Simple linear regression
    const n = xData.length;
    const sumX = xData.reduce((a, b) => a + b, 0);
    const sumY = yData.reduce((a, b) => a + b, 0);
    const sumXY = xData.map((x, i) => x * yData[i]).reduce((a, b) => a + b, 0);
    const sumX2 = xData.map((x) => x * x).reduce((a, b) => a + b, 0);

    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;

    const trendData = [
      [data[0].x ?? data[0].name, intercept],
      [data[data.length - 1].x ?? data[data.length - 1].name, slope * (n - 1) + intercept],
    ];

    return {
      type: 'line',
      color: this.colorForValue(trendData[1][1] - trendData[0][1]),
      data: trendData,
      dashStyle: 'Dash',
      marker: {
        enabled: false,
      },
      states: {
        hover: {
          lineWidth: 0,
        },
      },
      showInLegend: false,
      enableMouseTracking: false,
      zIndex: 0,
      custom: {
        slope,
        intercept,
      },
    };
  }

  private createChartSeriesForSmartScore(metric, groupBy) {
    const data = [];

    switch (groupBy) {
      case 'keymoments':
        this.vectorMetrics()
          .find((m) => m.scoreName === metric)
          .keyMoments.forEach((km, idx) => {
            const dataPoint: any = {
              name: km.keyMomentId,
              y: km.keyMomentChangeValue,
              custom: {
                sectionIds: this.generateSectionIdRangeFromInternalId(
                  km.sectionStart,
                  km.sectionEnd
                ),
                justification: km.justification,
              },
            };

            if (idx) {
              dataPoint.y += data[idx - 1].y;
            }

            dataPoint.color = this.colorForValue(dataPoint.y);
            data.push(dataPoint);
          });
        break;
      case 'segments':
        data.push(
          ...this.extractDataForSegment(
            (s) => s.keyMoments?.some((km) => km.scoreName === metric),
            (s) => s.keyMoments.find((km) => km.scoreName === metric).sectionChangeValue
          )
        );
        break;
      case 'timestamps':
        this.sections()
          .filter((s) => s.keyMoments?.some((km) => km.scoreName === metric))
          .forEach((s, idx) => {
            const y = s.keyMoments.find((km) => km.scoreName === metric).sectionChangeValue;
            const dataPoint = {
              x: s.date.getTime(),
              y,
              color: this.colorForValue(y),
              custom: {
                sectionIds: [s.sectionId],
              },
            };

            if (idx) {
              dataPoint.y += data[idx - 1].y;
            }

            data.push(dataPoint);
          });
        break;
    }

    this.totalChangeValue.set(
      this.vectorMetrics().find((m) => m.scoreName === this.selectedMetric())?.totalChangeValue
    );

    return [
      {
        type: 'line' as const,
        data,
      },
    ];
  }

  private extractDataForSegment(
    filterFn: (DocumentViewSection) => boolean,
    scoreExtractorFn: (DocumentViewSection) => number
  ) {
    const scoreBySegments: {
      [segment: string]: { count: number; score: number; sectionIds: number[] };
    } = this.sections()
      .filter(filterFn)
      .reduce((acc, s) => {
        s.segment?.forEach((segment) => {
          acc[segment] ??= {
            count: 0,
            score: 0,
            sectionIds: [],
          };
          acc[segment].count++;
          acc[segment].score += scoreExtractorFn(s);
          acc[segment].sectionIds.push(s.sectionId);
        });
        return acc;
      }, {});

    return Object.entries(scoreBySegments).map(([segment, segmentData]) => {
      const y = segmentData.score / segmentData.count;
      return {
        name: segment,
        y,
        color: this.colorForValue(y),
        custom: {
          sectionIds: segmentData.sectionIds,
        },
      };
    });
  }

  private colorForValue(y) {
    if (y > 0) {
      return SENTIMENT_HEX_COLOR_3_SCALE.positive;
    }
    if (y < 0) {
      return SENTIMENT_HEX_COLOR_3_SCALE.negative;
    }
    return SENTIMENT_HEX_COLOR_3_SCALE.neutral;
  }

  private generateSectionIdRangeFromInternalId(start, end) {
    /* eslint-disable eqeqeq */
    const startSection = this.sections().find((s) => start == (s.internalId ?? s.sectionId));
    const endSection = this.sections().find((s) => end == (s.internalId ?? s.sectionId));
    /* eslint-enable eqeqeq */
    if (!startSection || !endSection) {
      return [];
    }

    return Array.from(
      { length: endSection.sectionId - startSection.sectionId },
      (_, i) => startSection.sectionId + i
    );
  }

  private onPointClicked(point) {
    const sectionId = point.options.custom.sectionIds[0];
    this.selectSection.emit(this.sections().find((s) => s.sectionId === sectionId));
  }

  get hasSectionSentiment() {
    return this.sections().some((s) => s.sentimentScore != null);
  }

  get hasSectionSegment() {
    return this.sections().some((s) => s.segment?.length);
  }

  get hasSectionTimestamp() {
    return this.sections().some((s) => s.date);
  }
}
